/**
 * The analysis session: everything the analysis worker does, as plain core code (W2-12a checker-runtime;
 * ARCHITECTURE.md §9; DESIGN.md §5.10; worker protocol `AnalysisWorkerSchema`). `src/workers/analysis.worker.ts`
 * only wires it to the RPC server, real timers, the rule glob and the persisted assets; tests drive it directly
 * with a manual clock.
 *
 *  - Replica: `init` and `resync` build the document replica from a serialized snapshot (with the engine, purpose
 *    'check'); `txn` applies committed transactions strictly in order. A sequence gap, or ops that do not fit the
 *    replica, make the session ask for a resync (`needResync { lastSeq }`, once) and ignore transactions until the
 *    snapshot arrives. An `init` whose snapshot cannot be read drops the previous project's replica and asks for a
 *    resync the same way (lastSeq -1), so the new project's transactions never land on the old replica.
 *  - Scheduling: a pass starts 400 ms after the last transaction (`PASS_DELAY_MS`), at once after `init`,
 *    `resync` and `fullCheck`, and 30 ms after `configure` (settings sliders send bursts). Every request bumps a
 *    generation counter. A pass runs in slices of about `SLICE_MS` and yields to the event loop between slices, so
 *    newer transactions are received while it runs; a pass whose generation is no longer current is stale and stops
 *    at its next slice boundary (the newer request schedules its own pass).
 *  - Incremental passes (UX-092): the DirtyScopes of every transaction applied since the last pass are accumulated
 *    into a `PassScope`, and the runner re-runs only the rules and lines that change can affect (runner.ts); `init`,
 *    `resync`, `configure` and `fullCheck` check everything. A stale pass hands its scope back to the next one.
 *  - Results: what the rules found is sent as it comes (`problems` diffs by problem id, at most every `PARTIAL_MS`
 *    while a pass runs), so a long first pass on a big chart fills the list progressively. The Phira rules run first;
 *    when they are done the session sends `stats` with `phase: 'phira'` (stats extension, change request
 *    CR-W3-08-2): the problems that can block an export are known, the gameplay and style rules go on. Every completed
 *    pass ends with `stats` without a phase (note, line and key counts, check time).
 *  - Project switches: `init` (another project, or the same one reopened, on the app-wide worker) forgets the
 *    previous result, so the first pass of the new project reports every problem as added, and at once sends a
 *    RESET diff: `removed` starts with `PROBLEMS_RESET_ID` (never a problem id: ids are `${rule}:${key}`) followed
 *    by every id the previous project had. It is emitted while `init` is handled, so it reaches the main thread
 *    after every event of the previous project and before any of the new one: the problems service drops events
 *    until it sees it (change request CR-W2-12a-4).
 *  - Request tokens (CR-W2-12a-4): the problems service adds `req` (a growing number) to `init`, `txn`, `resync`
 *    and `configure`; the session remembers the last one received, and the stats of a pass carry the token that
 *    was current when the pass STARTED (`req`), so the main thread knows whether a pass covered its latest change
 *    ("Checking..." and "Fixed. Checking again" stay until it did). Stats also list the rules that threw in the
 *    pass (`failedRules`; their earlier problems are kept by the runner).
 *  - Configuration: thresholds, enabled rules, level, Phira player settings, skin metrics, reference viewport and
 *    song length (re-sent by the app when they change), plus the preview's shader compile errors (configure
 *    extension `shaderErrors`, change request CR-W2-12a-1) for PH-33.
 *  - Custom shader sources for PH-07: the session asks the host for the text of every custom shader asset the
 *    chart uses (assets are content-addressed, so a text is loaded once) and re-runs the check when one arrives.
 */

import { deserializeDoc } from '../doc/serialize';
import type { PhiraPlayerSettings, SkinMetrics } from '../engine/types';
import type { AssetId, ChartDoc, SerializedChartDoc } from '../model/types';
import type { Op } from '../ops/types';
import {
  CHECK_ENVIRONMENT_DEFAULTS,
  createCheckReplica,
  createReplicaContext,
  type CheckReplica,
  type CheckReplicaOptions,
} from './context';
import { CHECK_THRESHOLD_DEFAULTS } from './geometry';
import {
  createSteppedCheckRunner,
  isEmptyDiff,
  PassScope,
  type CheckEnvironment,
  type CheckLevel,
  type SteppedCheckRunner,
} from './runner';
import type { CheckRule, CheckThresholds, ProblemDiff } from './types';

/** Delay of the full pass after the last committed transaction (DESIGN.md §5.10). */
export const PASS_DELAY_MS = 400;
/** Delay after a configuration change (coalesces bursts such as a threshold slider). */
export const CONFIGURE_DELAY_MS = 30;
/** Work per slice of a pass before it yields to the event loop. */
export const SLICE_MS = 12;
/** Partial results of a long pass are sent at most this often (the Phira phase's end is sent at once). */
export const PARTIAL_MS = 250;

/**
 * First entry of `removed` in the diff an `init` sends: "forget every problem" (the previous project's result).
 * Problem ids are `${rule}:${entityKey}`, so this never names a problem.
 */
export const PROBLEMS_RESET_ID = '#reset';

/** True for the reset diff of an `init` (see PROBLEMS_RESET_ID). */
export function isResetDiff(d: ProblemDiff): boolean {
  return d.removed[0] === PROBLEMS_RESET_ID;
}

/** Request token the problems service adds to its requests (CR-W2-12a-4). */
export interface RequestToken {
  readonly req?: number;
}

export interface AnalysisInitParams extends RequestToken {
  readonly projectId: string;
  readonly doc: SerializedChartDoc;
  readonly seq: number;
  readonly thresholds: CheckThresholds;
  readonly enabledRules: readonly string[];
  readonly songLengthSec: number | null;
  /** The asset songLengthSec was decoded from (CheckContext.songLengthAsset). */
  readonly songLengthAsset?: AssetId | null;
  readonly player: PhiraPlayerSettings;
  readonly skin: SkinMetrics;
  readonly refViewport: { readonly w: number; readonly h: number };
  /** Experience level of the user (init extension, CR-W2-12a-4); absent keeps the current one ('full' at first). */
  readonly level?: CheckLevel;
}

/** Shader compile errors reported by the preview (AnalysisWorkerSchema.configure `shaderErrors`, CR-W2-12a-1). */
export interface ShaderErrorReport {
  readonly asset: AssetId;
  readonly log: string;
}

export interface AnalysisTxnParams extends RequestToken {
  readonly seq: number;
  readonly ops: readonly Op[];
}

export interface AnalysisResyncParams extends RequestToken {
  readonly doc: SerializedChartDoc;
  readonly seq: number;
}

export interface AnalysisConfigureParams extends RequestToken {
  readonly thresholds?: CheckThresholds;
  readonly enabledRules?: readonly string[];
  readonly songLengthSec?: number | null;
  readonly songLengthAsset?: AssetId | null;
  readonly level?: CheckLevel;
  readonly player?: PhiraPlayerSettings;
  readonly skin?: SkinMetrics;
  readonly refViewport?: { readonly w: number; readonly h: number };
  /** Replaces the list of custom shaders that failed to compile in the preview. */
  readonly shaderErrors?: readonly ShaderErrorReport[];
}

export interface AnalysisStats {
  readonly notes: number;
  readonly realNotes: number;
  readonly lines: number;
  readonly keys: number;
  readonly checkMs: number;
  /** Request token current when the pass started (stats extension, CR-W2-12a-4); absent before any token. */
  readonly req?: number;
  /** Rules that threw in the pass (their earlier problems are kept); absent when none did. */
  readonly failedRules?: readonly string[];
  /**
   * 'phira': sent DURING a pass, once its Phira rules (every rule that can block an export) are done; the other
   * rules go on and a final stats without a phase follows. Absent: the pass finished (stats extension, CR-W3-08-2).
   */
  readonly phase?: 'phira';
}

/** What the session needs from its host (the worker, or a test). */
export interface AnalysisHost {
  problems(diff: ProblemDiff): void;
  needResync(lastSeq: number): void;
  stats(s: AnalysisStats): void;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
  /** Milliseconds (monotonic). */
  now(): number;
  /** Resolves after pending messages were handled (a macrotask). */
  yieldToEvents(): Promise<void>;
  /** UTF-8 text of a custom shader asset, null when it cannot be read. */
  loadShaderSource?(asset: AssetId): Promise<string | null>;
  reportError?(where: string, error: unknown): void;
}

export interface AnalysisSessionState {
  readonly initialised: boolean;
  readonly projectId: string | null;
  /** Sequence number of the replica (-1 before the first snapshot). */
  readonly seq: number;
  readonly generation: number;
  readonly awaitingResync: boolean;
  /** A pass is waiting for its timer. */
  readonly scheduled: boolean;
  /** Passes started and not yet finished or abandoned. */
  readonly running: number;
  /** Passes completed (committed). */
  readonly completed: number;
  /** Passes abandoned because a newer request made them stale. */
  readonly abandoned: number;
}

export interface AnalysisSession {
  init(p: AnalysisInitParams): void;
  txn(p: AnalysisTxnParams): void;
  resync(p: AnalysisResyncParams): void;
  configure(p: AnalysisConfigureParams): void;
  fullCheck(): void;
  dispose(): void;
  readonly state: AnalysisSessionState;
  readonly runner: SteppedCheckRunner | null;
  readonly replica: CheckReplica | null;
}

export interface AnalysisSessionOptions {
  readonly replica?: CheckReplicaOptions;
}

export function createAnalysisSession(
  rules: readonly CheckRule[],
  host: AnalysisHost,
  opts: AnalysisSessionOptions = {},
): AnalysisSession {
  let replica: CheckReplica | null = null;
  let runner: SteppedCheckRunner | null = null;
  let projectId: string | null = null;
  let initialised = false;
  let enabledRules: readonly string[] = rules.map((r) => r.id);
  let thresholds: CheckThresholds = CHECK_THRESHOLD_DEFAULTS;
  let env: CheckEnvironment = CHECK_ENVIRONMENT_DEFAULTS;
  let level: CheckLevel = 'full';

  let generation = 0;
  let timer: unknown = null;
  /** What changed since the last pass started (handed to the next pass). */
  let scope = PassScope.full();
  let awaitingResync = false;
  let running = 0;
  let completed = 0;
  let abandoned = 0;
  let disposed = false;
  /** Last request token received (undefined until a request carried one). */
  let lastReq: number | undefined;

  const shaderSources = new Map<AssetId, string>();
  const shaderPending = new Set<AssetId>();
  const shaderFailed = new Set<AssetId>();
  let shaderErrors = new Map<AssetId, string>();

  const report = (where: string, error: unknown): void => {
    host.reportError?.(where, error);
  };

  const configureRunner = (): void => {
    if (runner === null) return;
    runner.setRules(rules, new Set(enabledRules));
    runner.setThresholds(thresholds);
    runner.setEnvironment(env);
    runner.setLevel(level);
  };

  const ensureReplica = (doc: SerializedChartDoc, seq: number): boolean => {
    let parsed: ChartDoc;
    try {
      parsed = deserializeDoc(doc);
    } catch (error) {
      report('snapshot', error);
      return false;
    }
    if (replica === null) {
      const r = createCheckReplica(parsed, seq, opts.replica);
      replica = r;
      runner = createSteppedCheckRunner(
        createReplicaContext(r, { shaderSources: () => shaderSources, shaderErrors: () => shaderErrors }),
        {
          onRuleError: (f) => {
            report(f.line === undefined ? `rule ${f.rule}` : `rule ${f.rule} (line ${String(f.line)})`, f.error);
          },
        },
      );
    } else {
      replica.reset(parsed, seq);
    }
    configureRunner();
    return true;
  };

  const clearTimer = (): void => {
    if (timer !== null) {
      host.clearTimer(timer);
      timer = null;
    }
  };

  const cancel = (): void => {
    generation++;
    clearTimer();
  };

  const noteRequest = (p: RequestToken): void => {
    if (typeof p.req === 'number' && Number.isFinite(p.req)) lastReq = p.req;
  };

  /**
   * Forgets the current result (the next pass reports every problem as added) and tells the main thread with the
   * reset diff, before anything of the new project can be sent.
   */
  const forgetResult = (): void => {
    const previous = runner?.problems().map((p) => p.id) ?? [];
    runner?.reset();
    host.problems({ added: [], removed: [PROBLEMS_RESET_ID, ...previous], updated: [] });
  };

  const runPass = async (gen: number): Promise<void> => {
    const r = runner;
    const rep = replica;
    /** A newer request (or dispose) made this pass stale. */
    const stale = (): boolean => gen !== generation || disposed;
    if (stale() || r === null || rep === null) return;
    // Every request received so far is part of what this pass checks (a later one makes it stale).
    const covers = lastReq;
    // This pass covers what changed so far; later changes start a new scope.
    const passScope = scope;
    scope = new PassScope();
    running++;
    let pass: ReturnType<SteppedCheckRunner['begin']> | null = null;
    let finished = false;
    try {
      let cpu = 0;
      // Compile what the transactions since the last pass changed (purpose 'check' compiles eagerly), then check.
      const c0 = host.now();
      rep.engine.sync();
      cpu += host.now() - c0;
      pass = r.begin(passScope);
      const facts = (extra: Partial<AnalysisStats>): AnalysisStats => {
        const s = rep.engine.stats();
        const failedRules = pass?.failedRules ?? [];
        return {
          notes: s.notes,
          realNotes: s.realNotes,
          lines: s.lines,
          keys: s.keys,
          checkMs: cpu,
          ...(covers === undefined ? {} : { req: covers }),
          ...(failedRules.length === 0 ? {} : { failedRules }),
          ...extra,
        };
      };
      let blockingSent = false;
      let lastFlush = host.now();
      /**
       * Before a yield: sends what the rules found so far when the Phira phase just ended (with its stats) or
       * PARTIAL_MS after the last time (a pass that ends within its first slice sends only its final result).
       */
      const flush = (): void => {
        if (pass === null) return;
        const blockingNow = !blockingSent && pass.blockingDone && !pass.done;
        const now = host.now();
        // What the Phira rules found goes out with the end of their phase (the export reads it right then).
        const send = now - lastFlush >= PARTIAL_MS || (blockingNow && pass.blockingUnits > 0);
        if (send) {
          lastFlush = now;
          const partial = pass.takeDiff();
          if (!isEmptyDiff(partial)) host.problems(partial);
        }
        if (blockingNow) {
          blockingSent = true;
          host.stats(facts({ phase: 'phira' }));
        }
      };
      for (;;) {
        const t0 = host.now();
        const done = pass.run(() => host.now() - t0 >= SLICE_MS);
        cpu += host.now() - t0;
        if (done) break;
        flush();
        await host.yieldToEvents();
        if (stale()) {
          abandoned++;
          return;
        }
      }
      const diff = pass.commit();
      finished = true;
      completed++;
      if (!isEmptyDiff(diff)) host.problems(diff);
      host.stats(facts({}));
    } catch (error) {
      report('pass', error);
    } finally {
      running--;
      // An abandoned (or failed) pass did not check everything it was given: the next pass does.
      if (!finished) scope.merge(pass?.scope ?? passScope);
    }
  };

  const schedule = (delayMs: number): void => {
    cancel();
    if (disposed || replica === null || runner === null || awaitingResync || !initialised) return;
    const gen = generation;
    timer = host.setTimer(() => {
      timer = null;
      void runPass(gen);
    }, delayMs);
  };

  /** A new input arrived (a shader text, or a request to cover): re-check soon without cutting short a debounce. */
  const refresh = (): void => {
    if (timer !== null) return;
    schedule(0);
  };

  /** Everything is checked by the next pass. */
  const checkEverything = (): void => {
    scope = PassScope.full();
  };

  /**
   * Forgets the replica and its runner (after an `init` whose snapshot could not be read; the runner's result was
   * already forgotten). Shader texts stay cached: assets are content-addressed.
   */
  const dropReplica = (): void => {
    replica?.dispose();
    replica = null;
    runner = null;
  };

  const requestResync = (): void => {
    if (awaitingResync) return;
    awaitingResync = true;
    cancel();
    host.needResync(replica?.seq ?? -1);
  };

  const loadShaders = (): void => {
    const rep = replica;
    if (host.loadShaderSource === undefined || rep === null) return;
    for (const effect of rep.doc.effects.values()) {
      if (effect.shader.kind !== 'asset') continue;
      const id = effect.shader.asset;
      if (shaderSources.has(id) || shaderPending.has(id) || shaderFailed.has(id)) continue;
      const asset = rep.doc.assets.get(id);
      if (asset === undefined || asset.missing === true) continue;
      shaderPending.add(id);
      host.loadShaderSource(id).then(
        (text) => {
          shaderPending.delete(id);
          if (text === null) {
            shaderFailed.add(id);
            return;
          }
          shaderSources.set(id, text);
          // PH-07 reads the shader texts: rules of effects and assets check again.
          scope.assets = true;
          scope.effects = true;
          refresh();
        },
        (error: unknown) => {
          shaderPending.delete(id);
          shaderFailed.add(id);
          report(`shader ${id}`, error);
        },
      );
    }
  };

  return {
    init(p) {
      if (disposed) return;
      noteRequest(p);
      // A pass of the previous project must never commit after this point.
      cancel();
      forgetResult();
      projectId = p.projectId;
      thresholds = p.thresholds;
      enabledRules = p.enabledRules;
      env = {
        player: p.player,
        skin: p.skin,
        refViewport: p.refViewport,
        songLengthSec: p.songLengthSec,
        songLengthAsset: p.songLengthAsset ?? null,
      };
      if (p.level !== undefined) level = p.level;
      awaitingResync = false;
      shaderFailed.clear();
      if (!ensureReplica(p.doc, p.seq)) {
        // The replica still holds the previous project: never check it under the new one, and never apply the new
        // project's transactions to it. Drop it (its result was already forgotten above) and ask for a snapshot;
        // transactions are ignored until it arrives, and `resync` builds a fresh replica for this project.
        initialised = false;
        dropReplica();
        requestResync();
        return;
      }
      initialised = true;
      checkEverything();
      loadShaders();
      schedule(0);
    },
    txn(p) {
      if (disposed) return;
      noteRequest(p);
      if (awaitingResync) return;
      if (replica === null || !initialised) {
        // After an init (no replica yet, or one that could not be built): only a snapshot can make it usable.
        if (projectId !== null) requestResync();
        return;
      }
      const r = replica.apply(p.seq, p.ops);
      switch (r.kind) {
        case 'stale':
          // Already part of the replica; a pass still has to cover the request (the sender waits for it).
          refresh();
          return;
        case 'gap':
          requestResync();
          return;
        case 'failed':
          report(`txn ${String(p.seq)}`, r.error);
          requestResync();
          return;
        case 'applied':
          scope.addChange(r.dirty, p.ops, replica.doc.notes);
          if (r.dirty.effects || r.dirty.assets) loadShaders();
          schedule(PASS_DELAY_MS);
          return;
      }
    },
    resync(p) {
      if (disposed) return;
      noteRequest(p);
      if (!ensureReplica(p.doc, p.seq)) return;
      // A snapshot after a failed init makes the session usable for that project.
      if (projectId !== null) initialised = true;
      awaitingResync = false;
      shaderFailed.clear();
      checkEverything();
      loadShaders();
      schedule(0);
    },
    configure(p) {
      if (disposed) return;
      noteRequest(p);
      if (p.thresholds !== undefined) thresholds = p.thresholds;
      if (p.enabledRules !== undefined) enabledRules = p.enabledRules;
      if (p.level !== undefined) level = p.level;
      env = {
        player: p.player ?? env.player,
        skin: p.skin ?? env.skin,
        refViewport: p.refViewport ?? env.refViewport,
        songLengthSec: p.songLengthSec !== undefined ? p.songLengthSec : env.songLengthSec,
        songLengthAsset: p.songLengthAsset !== undefined ? p.songLengthAsset : env.songLengthAsset,
      };
      if (p.shaderErrors !== undefined) {
        shaderErrors = new Map(p.shaderErrors.map((e) => [e.asset, e.log]));
      }
      configureRunner();
      checkEverything();
      schedule(CONFIGURE_DELAY_MS);
    },
    fullCheck() {
      checkEverything();
      schedule(0);
    },
    dispose() {
      disposed = true;
      cancel();
      replica?.dispose();
    },
    get state() {
      return {
        initialised,
        projectId,
        seq: replica?.seq ?? -1,
        generation,
        awaitingResync,
        scheduled: timer !== null,
        running,
        completed,
        abandoned,
      };
    },
    get runner() {
      return runner;
    },
    get replica() {
      return replica;
    },
  };
}
